import React, { useCallback } from 'react';
import { Tooltip } from '@mui/material';
import {
	CanvasEntityDisabledReason,
	EntityState,
} from '@elyx-code/project-logic-tree';
import {
	EntityTypeIconColor,
	EntityTypeIconSize,
	resolveEntityIcon,
} from '../../services/editor/icons';
import './entity-icon.css';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { EditorService } from '../../services/editor';
import { AbsoluteCanvasObject, RelativeObject } from '../../lib/canvas';

interface IEntityIconProps {
	entity: EntityState;
	size?: EntityTypeIconSize;
	color?: EntityTypeIconColor;
	style?: React.CSSProperties;
	parentDisabledReasons?: (CanvasEntityDisabledReason | string)[];
	className?: string;
	project?: EditorService;
	parentCanvasObject?: AbsoluteCanvasObject | RelativeObject;
	popupPlacement?:
		| 'left'
		| 'right'
		| 'top'
		| 'bottom'
		| 'bottom-end'
		| 'bottom-start'
		| 'left-end'
		| 'left-start'
		| 'right-end'
		| 'right-start'
		| 'top-end'
		| 'top-start';
}

const EntityIcon: React.FC<IEntityIconProps> = ({
	entity,
	size = 'medium',
	color = 'black',
	style = {},
	parentDisabledReasons = [],
	className = '',
	project,
	parentCanvasObject,
	popupPlacement,
}) => {
	const config = resolveEntityIcon(entity, color);
	const src = config.file;

	const id = 'entity-icon--' + entity.id;

	const tooltipBody = <>Data type</>;

	// For now disabled hardcoded
	const disabled = !!parentDisabledReasons.length || true;

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={[
					'tooltip',
					'tooltip--large-font',
					'tooltip--dynamic-value',
				]}
				id={id + '--popup--body'}
			>
				{tooltipBody}
			</CanvasPopupBaseComponent>
		),
		[id],
	);

	const isCanvas = !!parentCanvasObject && !!project;

	// Only if there is a 'parentCanvasObject' prop, we should use the canvasPopup
	// Otherwise, we should use the Tooltip component
	const canvasPopup = isCanvas
		? useCanvasPopup(
				{
					editor: project,
					id: id + '--popup',
					anchorQuerySelector: '#' + id,
					arrowClasses: ['dynamic-value-popup-arrow'],
					parentCanvasObject: parentCanvasObject,
					arrowContainerSelector: '#' + id + '--popup--body',
					placement: popupPlacement || 'bottom',
				},
				memoizedCanvasPopupBody,
				[id],
			)
				.setupHover({
					closeDelay: 80,
					openDelay: 600,
				})
				.onBeforeShow((e) => {
					const otherPopup =
						CanvasPopup.globalCanvasPopups[
							id + '--popup-dynamic-value-switch-button'
						];

					if (otherPopup && otherPopup.open) {
						return false;
					}

					if (disabled) {
						return false;
					}

					return true;
				})
		: null;

	if (!!src) {
		return (
			<Tooltip
				// The styles overrides below can be cleaned up
				classes={{
					tooltip: 'tooltip tooltip--dynamic-value',
					arrow: 'mui-tooltip-arrow',
				}}
				slotProps={{
					popper: {
						className: 'tooltip tooltip--dynamic-value',
						style: {
							margin: 0,
							padding: 0,
						},
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: [0, 8],
								},
							},
						],
					},
					tooltip: {
						className: 'tooltip tooltip--dynamic-value',
						style: {
							margin: 0,
							padding: 5,
						},
					},
					arrow: {
						className: 'mui-tooltip-arrow',
					},
				}}
				arrow
				title={!isCanvas && !disabled ? tooltipBody : ''}
				placement={popupPlacement || 'bottom'}
				leaveDelay={400}
			>
				<img
					className={
						`entity-icon img-entity-icon data-type-icon-${size} ` +
						(className || '')
					}
					src={src}
					alt={`${entity?.type || 'unknown node type'} icon`}
					ref={canvasPopup?.anchorRef}
					style={{
						opacity: !!parentDisabledReasons.length ? 0.5 : 1,
						...style,
					}}
				/>
			</Tooltip>
		);
	}

	if (!!config.url) {
		return (
			<Tooltip
				// The styles overrides below can be cleaned up
				classes={{
					tooltip: 'tooltip tooltip--dynamic-value',
					arrow: 'mui-tooltip-arrow',
				}}
				slotProps={{
					popper: {
						className: 'tooltip tooltip--dynamic-value',
						style: {
							margin: 0,
							padding: 0,
						},
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: [0, 8],
								},
							},
						],
					},
					tooltip: {
						className: 'tooltip tooltip--dynamic-value',
						style: {
							margin: 0,
							padding: 5,
						},
					},
					arrow: {
						className: 'mui-tooltip-arrow',
					},
				}}
				arrow
				title={!isCanvas && !disabled ? tooltipBody : ''}
				placement={popupPlacement || 'bottom'}
				leaveDelay={400}
			>
				<img
					className={
						`entity-icon img-entity-icon data-type-icon-${size} external-icon-img ` +
						(className || '')
					}
					src={config.url}
					ref={canvasPopup?.anchorRef}
					alt={`${entity?.type || 'unknown node type'} icon`}
					style={{
						opacity: !!parentDisabledReasons.length ? 0.5 : 1,
						...style,
					}}
				/>
			</Tooltip>
		);
	}

	if (!!config.fa) {
		return (
			<Tooltip
				// The styles overrides below can be cleaned up
				classes={{
					tooltip: 'tooltip tooltip--dynamic-value',
					arrow: 'mui-tooltip-arrow',
				}}
				slotProps={{
					popper: {
						className: 'tooltip tooltip--dynamic-value',
						style: {
							margin: 0,
							padding: 0,
						},
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: [0, 8],
								},
							},
						],
					},
					tooltip: {
						className: 'tooltip tooltip--dynamic-value',
						style: {
							margin: 0,
							padding: 5,
						},
					},
					arrow: {
						className: 'mui-tooltip-arrow',
					},
				}}
				arrow
				title={!isCanvas && !disabled ? tooltipBody : ''}
				placement={popupPlacement || 'bottom'}
				leaveDelay={400}
			>
				<i
					className={
						`entity-icon data-type-icon-${size} fa-entity-icon fa-solid fa-${
							config.fa || 'rectangle-list'
						} ` + (className || '')
					}
					ref={canvasPopup?.anchorRef}
					style={{
						opacity: !!parentDisabledReasons.length ? 0.5 : 1,
						...style,
					}}
				/>
			</Tooltip>
		);
	}

	return null;
};

export default EntityIcon;
